import React, { useState, useMemo, useCallback } from 'react';
import { Plus, LayoutDashboard } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { setCurrentSite } from '../../redux/slices/siteSlice';
import { useListAdminSitesQuery, useListPortfolioSitesQuery } from '../../redux/api/portfolioApi';
import type { PortfolioSite } from '../../redux/api/portfolioApi';
import { usePortfolioEvents } from '../../hooks/usePortfolioEvents';

import DashboardModule from '../dashboard/DashboardModule';
import ModuleGrid from '../dashboard/ModuleGrid';
import { useModuleLayout, type ModuleConfig } from '../dashboard/useModuleLayout';
import AISummaryModule from '../dashboard/modules/AISummaryModule';
import SafetyIssuesModule from '../dashboard/modules/SafetyIssuesModule';
import DashboardTimelineModule from '../dashboard/modules/DashboardTimelineModule';
import SitesOverviewTable from '../dashboard/modules/SitesOverviewTable';
import TopEventsModule from '../dashboard/modules/TopEventsModule';
import ComponentsSummaryModule from '../dashboard/modules/ComponentsSummaryModule';
import CustomPortfolioEditor from '../dashboard/CustomPortfolioEditor';
import KnowledgeDropZone from '../dashboard/KnowledgeDropZone';
import PortfolioMapModule from '../dashboard/modules/PortfolioMapModule';
import UpcomingCalendarModule from '../dashboard/modules/UpcomingCalendarModule';
import { createMockUpcomingEvents, type UpcomingPortfolioEvent } from '../../mocks/upcomingEventsMock';

const DEFAULT_MODULES: ModuleConfig[] = [
  { id: 'map', title: 'Portfolio Map', collapsed: false, order: 0, span: 'half' },
  { id: 'ai', title: 'AI Summary', collapsed: false, order: 1, span: 'half' },
  { id: 'sites', title: 'Sites Overview', collapsed: false, order: 2, span: 'half' },
  { id: 'upcoming_calendar', title: 'Upcoming Events', collapsed: false, order: 3, span: 'half' },
  { id: 'safety', title: 'Safety Issues', collapsed: false, order: 4, span: 'full' },
  { id: 'timeline', title: 'Timeline', collapsed: false, order: 5, span: 'full' },
  { id: 'events', title: 'Top Events', collapsed: false, order: 6, span: 'half' },
  { id: 'components', title: 'Components', collapsed: false, order: 7, span: 'half' },
];

const PortfolioDashboardTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { modules, moveModule, toggleCollapse } = useModuleLayout('portfolio', DEFAULT_MODULES);

  const [showPortfolioEditor, setShowPortfolioEditor] = useState(false);
  const [showKnowledgeUpload, setShowKnowledgeUpload] = useState(false);
  const [customSiteIds, setCustomSiteIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('eg-custom-portfolio');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  // Org scoping for super admins
  const profile = useAppSelector((state) => state.auth.profile);
  const organizationState = useAppSelector((s) => s.organization);
  const isSuperAdmin = (profile.email || '').toLowerCase().endsWith('@engramiq.com');
  const currentOrgId = isSuperAdmin ? organizationState.currentOrganization?.id ?? null : null;

  const selectedPortfolioId = useAppSelector((state) => state.portfolio.selectedPortfolioId);
  const usePortfolioScoping = !!selectedPortfolioId;

  // Fetch sites scoped to selected portfolio or organization
  const adminArgs = currentOrgId ? { organization_id: currentOrgId, limit: 100 } : { limit: 100 };
  const { data: adminSitesData, isLoading: adminLoading } = useListAdminSitesQuery(adminArgs, {
    skip: !isSuperAdmin || usePortfolioScoping,
  });
  const { data: portfolioSitesData, isLoading: portfolioLoading } = useListPortfolioSitesQuery(
    { portfolio_id: selectedPortfolioId || '' },
    { skip: !selectedPortfolioId }
  );

  const sitesLoading = usePortfolioScoping ? portfolioLoading : adminLoading;
  const allSites = useMemo(() => {
    const raw = usePortfolioScoping ? portfolioSitesData : adminSitesData;
    if (!raw) return [] as PortfolioSite[];
    return (raw as any)?.items || [];
  }, [usePortfolioScoping, portfolioSitesData, adminSitesData]);

  // Apply custom portfolio filter
  const displaySites = useMemo(() => {
    if (customSiteIds.length === 0) return allSites;
    const idSet = new Set(customSiteIds);
    return allSites.filter(s => idSet.has(s.site_id || s.id || ''));
  }, [allSites, customSiteIds]);

  // Aggregate stats
  const totalCapacity = useMemo(
    () => displaySites.reduce((sum, s) => sum + (s.site_capacity_mw || s.capacity_mw || 0), 0),
    [displaySites]
  );

  // Fetch events from all portfolio sites
  const { events: portfolioEvents, siteNameMap } = usePortfolioEvents(displaySites);

  // Generate mock upcoming events for the first 5 sites
  const upcomingEvents: UpcomingPortfolioEvent[] = useMemo(() => {
    if (displaySites.length === 0) return [];
    return createMockUpcomingEvents(displaySites, 5);
  }, [displaySites]);

  const handleSiteClick = useCallback((site: PortfolioSite) => {
    dispatch(setCurrentSite(site));
    window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'siteanalysis' } }));
  }, [dispatch]);

  const handleSavePortfolio = useCallback((ids: string[]) => {
    setCustomSiteIds(ids);
    localStorage.setItem('eg-custom-portfolio', JSON.stringify(ids));
  }, []);

  const handleOpenTab = useCallback((tabType: string) => {
    window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType } }));
  }, []);

  const handleEventClick = useCallback((eventId: string) => {
    // Find the event's site and switch to it before navigating
    const event = portfolioEvents.find((e) => e.id === eventId);
    if (event?.site_id) {
      const site = displaySites.find((s) => (s.site_id || s.id) === event.site_id);
      if (site) dispatch(setCurrentSite(site));
    }
    handleOpenTab('eventstasks');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('navigateEventsTasksTab', { detail: { eventId } }));
    }, 150);
  }, [portfolioEvents, displaySites, dispatch, handleOpenTab]);

  const renderModule = useCallback((mod: ModuleConfig, dragHandleProps: Record<string, unknown>) => {
    switch (mod.id) {
      case 'sites':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            onViewAll={() => handleOpenTab('sitestable')}
            viewAllLabel="All Sites"
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            {sitesLoading ? (
              <div className="eg-loading">Loading sites...</div>
            ) : (
              <SitesOverviewTable
                sites={displaySites}
                onSiteClick={handleSiteClick}
                onEditPortfolio={() => setShowPortfolioEditor(true)}
                onViewAll={() => handleOpenTab('sitestable')}
              />
            )}
          </DashboardModule>
        );
      case 'ai':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <AISummaryModule variant="portfolio" onAskAI={() => {}} />
          </DashboardModule>
        );
      case 'safety':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            onViewAll={() => handleOpenTab('eventstasks')}
            viewAllLabel="Safety View"
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <SafetyIssuesModule events={portfolioEvents} showSiteName onEventClick={handleEventClick} />
          </DashboardModule>
        );
      case 'timeline':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            onViewAll={() => handleOpenTab('interactivetimeline')}
            viewAllLabel="Full Timeline"
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <DashboardTimelineModule events={portfolioEvents} sites={displaySites} siteNameMap={siteNameMap} onEventClick={handleEventClick} />
          </DashboardModule>
        );
      case 'upcoming_calendar':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            onViewAll={() => handleOpenTab('portfolioeventscalendar')}
            viewAllLabel="Full Calendar"
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <UpcomingCalendarModule
              events={upcomingEvents}
              onOpenCalendar={() => handleOpenTab('portfolioeventscalendar')}
            />
          </DashboardModule>
        );
      case 'map':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            onViewAll={() => handleOpenTab('portfoliomap')}
            viewAllLabel="Full Map"
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <PortfolioMapModule sites={displaySites} onSiteClick={handleSiteClick} />
          </DashboardModule>
        );
      case 'events':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            onViewAll={() => handleOpenTab('eventstasks')}
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <TopEventsModule
              events={portfolioEvents}
              upcomingEvents={upcomingEvents}
              showSiteName
              onEventClick={handleEventClick}
            />
          </DashboardModule>
        );
      case 'components':
        return (
          <DashboardModule
            id={mod.id}
            title={mod.title}
            collapsed={mod.collapsed}
            onToggleCollapse={() => toggleCollapse(mod.id)}
            onViewAll={() => handleOpenTab('portfoliocomponents')}
            viewAllLabel="Portfolio Components"
            dragHandleProps={dragHandleProps}
            className="eg-card-elevated"
          >
            <ComponentsSummaryModule sites={displaySites} />
          </DashboardModule>
        );
      default:
        return null;
    }
  }, [modules, portfolioEvents, upcomingEvents, displaySites, siteNameMap, toggleCollapse, handleOpenTab, sitesLoading]);

  return (
    <div className="h-full flex flex-col">
      {/* ═══ DARK HEADER ZONE ═══ */}
      <div
        className="shrink-0 relative overflow-hidden"
        style={{ background: `linear-gradient(160deg, var(--color-dash-header-bg) 0%, var(--color-dash-header-bg-subtle) 100%)` }}
      >
        {/* Subtle radial glow */}
        <div
          className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-[0.06] pointer-events-none"
          style={{ background: 'radial-gradient(circle, #17c480 0%, transparent 70%)' }}
        />

        <div className="relative px-6 pt-5 pb-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2.5">
                <LayoutDashboard className="w-5 h-5 text-[#17c480]" />
                <h1
                  className="text-[22px] font-bold tracking-tight"
                  style={{ color: 'var(--color-dash-header-text)' }}
                >
                  Portfolio Dashboard
                </h1>
              </div>
              <p className="mt-1.5 text-sm" style={{ color: 'var(--color-dash-header-secondary)' }}>
                {displaySites.length} Sites &middot; {totalCapacity.toFixed(0)} MW
                {customSiteIds.length > 0 && (
                  <span style={{ color: 'var(--color-dash-header-muted)' }}> &middot; Custom portfolio</span>
                )}
              </p>
            </div>
            <button
              onClick={() => setShowKnowledgeUpload(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-[#17c480] text-white hover:bg-[#17c480]/85 transition-all"
              style={{ boxShadow: '0 1px 3px rgba(23, 196, 128, 0.3)' }}
            >
              <Plus className="w-4 h-4" />
              Add Knowledge
            </button>
          </div>
        </div>

        {/* Accent divider */}
        <div
          className="h-[2px]"
          style={{
            background: 'linear-gradient(90deg, #17c480 0%, rgba(23, 196, 128, 0.3) 40%, transparent 80%)',
          }}
        />
      </div>

      {/* ═══ CONTENT ZONE ═══ */}
      <div
        className="flex-1 overflow-y-auto"
        style={{ background: 'var(--color-zone-recessed)' }}
      >
        <div className="px-6 py-5">
          <ModuleGrid
            modules={modules}
            onDragEnd={moveModule}
            renderModule={renderModule}
          />
        </div>
      </div>

      {/* Modals */}
      <CustomPortfolioEditor
        isOpen={showPortfolioEditor}
        onClose={() => setShowPortfolioEditor(false)}
        sites={allSites}
        selectedIds={customSiteIds}
        onSave={handleSavePortfolio}
      />

      <KnowledgeDropZone
        isOpen={showKnowledgeUpload}
        onClose={() => setShowKnowledgeUpload(false)}
        sites={allSites}
      />
    </div>
  );
};

export default PortfolioDashboardTab;
